import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { setSession, isAuthenticated } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // null = unknown yet; true = no accounts exist, show "create first account"
  const [needsBootstrap, setNeedsBootstrap] = useState(null);

  useEffect(() => {
    if (isAuthenticated()) navigate(next, { replace: true });
  }, [navigate, next]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/auth/bootstrap-status`)
      .then((r) => !cancelled && setNeedsBootstrap(Boolean(r.data?.needs_bootstrap)))
      .catch(() => !cancelled && setNeedsBootstrap(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const path = needsBootstrap ? 'register' : 'login';
      const { data } = await axios.post(`${API}/auth/${path}`, { email, password });
      setSession(data.access_token, data.user);
      navigate(next, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      setError(
        err?.response?.data?.detail ||
          (status === 401 ? 'Invalid email or password.' : 'Sign-in failed. Please try again.')
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-950 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-xl border border-neutral-800 bg-neutral-900 p-8 shadow-xl"
      >
        <h1 className="text-2xl font-semibold text-white">REIPR</h1>
        <p className="mt-1 mb-6 text-sm text-neutral-400">
          {needsBootstrap ? 'Create the first account' : 'Sign in to continue'}
        </p>

        <label className="block text-xs font-medium text-neutral-400" htmlFor="email">
          Email
        </label>
        <input
          id="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 mb-4 w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-white outline-none focus:border-neutral-500"
        />

        <label className="block text-xs font-medium text-neutral-400" htmlFor="password">
          Password
        </label>
        <input
          id="password"
          type="password"
          autoComplete={needsBootstrap ? 'new-password' : 'current-password'}
          required
          minLength={needsBootstrap ? 12 : undefined}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-white outline-none focus:border-neutral-500"
        />
        {needsBootstrap && (
          <p className="mt-1 text-xs text-neutral-500">Minimum 12 characters.</p>
        )}

        {error && (
          <p role="alert" className="mt-4 text-sm text-red-400">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="mt-6 w-full rounded-md bg-white px-4 py-2 font-medium text-black transition disabled:opacity-50"
        >
          {busy ? 'Working…' : needsBootstrap ? 'Create account' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
